import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../../constants/cypressIds';
import { AgentPortalDuskIds } from '../../../../constants/duskIds';
import { getValidationObject, validationTypes } from '../../../../constants/regexPatterns';
import { createErrorToast } from '../../../../redux/modules/app';
import { useUpdateAgent } from '../../../../services/insurance-request-service';
import CertificateCreationBlueprintType from '../../../../types/CertificateCreationBlueprintType';
import { getFormattedPhoneNumber, removePhoneNumberFormat } from '../../../../utils/formatPhoneUtil';
import FormInput from '../../../form/FormInput';
import { inputTypes } from '../../../form/FormInput/util';
import LoadingSpinner from '../../../Spinner';

import AgenciesTable from './AgenciesTable';

const AgentDetailsStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const { insuranceRequestId } = useParams();

  const company = useSelector(blueprint.selectCompany);
  const user = useSelector(blueprint.selectUser);
  const agencyId = useSelector(blueprint.selectAgencyId);
  const { chooseAnyPolicyType, requireAgentDetails } = useSelector(blueprint.selectOptions);

  const { mutate: updateAgentDetails } = useUpdateAgent({ insuranceRequestId, blueprint });

  const useFormMethods = useForm({
    defaultValues: {
      first: user?.userFirstName ?? null,
      last: user?.userLastName ?? null,
      phone: getFormattedPhoneNumber(user?.userPhone) ?? null
    }
  });
  const { handleSubmit } = useFormMethods;

  // useEffect for skipping this step if details are not required
  useEffect(() => {
    if (!requireAgentDetails) {
      dispatch(blueprint.nextStep());
    }
  }, [requireAgentDetails]);

  const onSubmit = async agentData => {
    if (!agencyId) {
      dispatch(createErrorToast({
        title: 'Please select an agency or company to continue',
        duration: 5000
      }));

      return false;
    }

    agentData.phone = removePhoneNumberFormat(agentData.phone);

    updateAgentDetails(agentData);

    dispatch(blueprint.nextStep());
  };

  const handleRowClick = (e, rowId) => {
    dispatch(blueprint.setAgencyId(rowId));
  };

  if (!requireAgentDetails) {
    return (
      <Box>
        <Text>Loading company settings...</Text>
        <LoadingSpinner minHeight="100px" />
      </Box>
    );
  }

  return (
    <Box mt="2.75rem" overflowY="auto" pb={8} px={6} width="100%">
      <Text textStyle="displayXs">Agent Details</Text>

      <Text fontSize="bodyXl" mb={4} mt={8}>
        {`${company.companyName} is requiring that you provide the following information.`}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          mx="50px"
          pt={8}
          px={8}
        >
          <Flex
            alignItems="center"
            flexFlow="row wrap"
            justifyContent="center"
            mb="20px"
            minH="100px"
          >
            <HStack>
              <FormInput
                cypressId={CypressIds.certAgentDetailsFirstNameInput}
                data-analytics-id="agent-portal-first-name-input"
                duskSelector={AgentPortalDuskIds.verifyFirstInput}
                inputName="first"
                labelName="First Name"
                maxW="400px"
                mx={4}
                placeholder="First Name"
                type={inputTypes.standard}
                useFormMethods={useFormMethods}
                validationRules={{ ...getValidationObject(validationTypes.alphaNumericAndCharacters), required: 'Please provide your first name.' }}
              />
              <FormInput
                cypressId={CypressIds.certAgentDetailsLastNameInput}
                data-analytics-id="agent-portal-last-name-input"
                duskSelector={AgentPortalDuskIds.verifyLastInput}
                inputName="last"
                labelName="Last Name"
                maxW="400px"
                mx={4}
                placeholder="Last Name"
                type={inputTypes.standard}
                useFormMethods={useFormMethods}
                validationRules={{ ...getValidationObject(validationTypes.alphaNumericAndCharacters), required: 'Please provide your last name.' }}
              />

              <FormInput
                cypressId={CypressIds.certAgentDetailsPhoneInput}
                data-analytics-id="agent-portal-phone-input"
                duskSelector={AgentPortalDuskIds.verifyPhoneInput}
                inputName="phone"
                labelName="Phone Number"
                maxW="400px"
                mx={4}
                placeholder="(XXX)XXX-XXXX"
                type={inputTypes.phoneNumber}
                useFormMethods={useFormMethods}
                validationRules={{ ...getValidationObject(validationTypes.phoneNumber), required: 'Please provide your phone number.' }}
              />
            </HStack>
          </Flex>

          <AgenciesTable handleRowClick={handleRowClick} selectedAgencyId={agencyId} />

        </Box>

        <Flex
          alignItems="center"
          flexFlow="row wrap"
          justifyContent="center"
          mb="20px"
          minH="100px"
        >
          <HStack>
            <Button
              variant="secondary"
              onClick={() => dispatch(blueprint.goBack(chooseAnyPolicyType ? 1 : 2))}
            >
              Back
            </Button>
            <Button
              data-cy={CypressIds.certAgentAgencyNextButton}
              type="submit"
            >
              Next Step
            </Button>
          </HStack>
        </Flex>
      </form>

    </Box>
  );
};

AgentDetailsStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default AgentDetailsStep;
